import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useLocation } from "react-router-dom";
import {
  getCookie,
  APROPO_CAMPAIGN_COOKIE_NAME,
  DEFAULT_EMAIL_SEARCH_PARAM,
  LANDING_EMAIL_SEARCH_PARAM,
  CID_SEARCH_PARAM,
} from "@utils";
import { registerUserQuery } from "@query";
import { useStores } from "@hooks";
import { RegisterForm } from "@forms";
import { Alert } from "@components";
import Grid from "@material-ui/core/Grid";
import { OrDivider } from "@views/Auth/components/OrDivider/OrDivider";
import { MicrosoftSSO } from "@views/Auth/components/MicrosoftSSO/MicrosoftSSO";

export const Register = () => {
  const { t } = useTranslation();
  const { userStore, stateStore } = useStores();
  const { code } = useParams();
  const { search } = useLocation();

  const [ alertText, setAlertText ] = useState();

  const defaultEmail = useMemo(() => (
    new URLSearchParams(search).get(DEFAULT_EMAIL_SEARCH_PARAM)
  ), []);

  const landingEmail = useMemo(() => (
    new URLSearchParams(search).get(LANDING_EMAIL_SEARCH_PARAM)
  ), []);

  // AD-765/AD-754: partner campaign attribution. The apropo_campaign cookie (Domain=.apropo.io,
  // set on the landing page) is the source of truth since it survives navigation across
  // apropo.io subdomains, unlike a query param on this one page. The ?cid= query param is a
  // fallback for a link pointing straight at /sign-up, mirroring landingEmail above.
  const campaignId = useMemo(() => (
    getCookie(APROPO_CAMPAIGN_COOKIE_NAME) || new URLSearchParams(search).get(CID_SEARCH_PARAM) || undefined
  ), []);

  const handleSignUp = async (data) => {
    try {
      const userData = await registerUserQuery({ ...data, campaignId }, code);
      if(!code)
        stateStore.setActivationEmail(userData.email);
      
      userStore.saveUserData(userData);
      userStore.checkWorkspacesAndRedirect(true);
      userStore.setLoginProcessStatus(true);
    } catch (e) {
      if (e.response?.data?.errors?.length) {
        const errors = e?.response?.data?.errors;
        if (errors) return { errors };
      } else if (e.response?.data?.error) setAlertText(t(e.response.data.error));
      else setAlertText(t("errors.network"));
    }
  };
  
  const handleAlertClose = () => {
    setAlertText(undefined)
  }

  return (
    <Grid
      container
      direction="column"
      wrap="nowrap"
      className="h-full"
    >
      <h1 className="w-full pb-8 text-center">
        { t("routes.create_account") }
      </h1>
      
      <RegisterForm
        onSubmit={ handleSignUp }
        isAccountActivation={ !!code }
        defaultEmail={ defaultEmail || landingEmail || undefined }
      />
      
      <OrDivider/>
      <MicrosoftSSO register/>
      
      <Alert
        isOpen={ Boolean(alertText) }
        title={ alertText }
        acceptText={ t("common.close") }
        onAccept={ handleAlertClose }
      />
    </Grid>
  );
};
